
import Browser from '../browser';
import LazyImage from '@/components/lazy-image';
import { track } from '@/utils/analytics';
import ImageHelper from '@/utils/ImageHelper';
import { Comparator } from '@/ui-kit/enums';
import { StyleSizeType, StyleFontWeightType, StyleColorSchemeType } from '@/ui-kit/styles/enums';
import Typography from '@/ui-kit/components/typography';
import { TypographyType } from '@/ui-kit/components/typography/enums';
import Button from '@/ui-kit/components/button';
import ExternalLink from '@/ui-kit/components/external-link';

/**
   * @component HowItWorksSectionComponent
   *
   * @prop {String} title
   * @prop {String} description
   * @prop {Array} images
   * @prop {String} attachment
   * @private
   */

export default {
  name: 'HowItWorksSectionComponent',
  components: {
    Browser,
    LazyImage,
    Typography,
    Button,
    ExternalLink,
  },
  props: {
    province: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
    images: {
      type: Array,
      default: () => [],
    },
    attachments: {
      type: Array,
      default: () => [],
    },
    caption: {
      type: String,
      default: null,
    },
    link: {
      type: String,
      default: null,
    },
    href: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      selected: 0,
      StyleFontWeightType,
      StyleColorSchemeType,
      TypographyType,
      StyleSizeType,
    };
  },
  computed: {
    document() {
      if (this.attachment.name) {
        return `Download ${this.attachment.name.replace(/[-_.]/g, ' ').replace('pdf', 'PDF')}`;
      }
      return null;
    },
    selectedImage() {
      return this.images[this.selected];
    },
    selectedImageRatio() {
      return ImageHelper.constructor.getImageRatio(this.selectedImage.dimensions);
    },
    selectedImageSizes() {
      return [
        {
          comparator: Comparator.Less,
          breakpoint: StyleSizeType.XSmall,
          imageSize: 400,
        },
        {
          comparator: Comparator.Less,
          breakpoint: StyleSizeType.Small,
          imageSize: 600,
        },
        {
          comparator: Comparator.Greater,
          breakpoint: StyleSizeType.Small,
          imageSize: 900,
        },
        {
          comparator: Comparator.Greater,
          breakpoint: StyleSizeType.Medium,
          imageSize: 600,
        },
      ];
    },
  },
  methods: {
    previous() {
      this.selected = (this.selected - 1) % this.images.length;
      this.trackUserImageScrollPrevious();
    },

    next() {
      this.selected = (this.selected + 1) % this.images.length;
      this.trackUserImageScrollNext();
    },

    documentName(document) {
      return document.name
        ? `Download ${document.name.replace(/[-_.]/g, ' ').replace('pdf', 'PDF')}`
        : null;
    },

    /**
        * @track Tracks the previous scrolling of a specific section's image gallery
        * and which page it was executed in Mixpanel (via Segment).
        *
        * @returns {void}
        * @private
        */

    trackUserImageScrollPrevious() {
      const { path } = this.$route;
      if (path === '/how-it-works/seller') {
        track(`How it works_Seller_${this.title} scroll Previous`);
      } else if (path === '/how-it-works/buyer') {
        track(`How it works_Buyer_${this.title} scroll Previous`);
      } else {
        track(`How it works_Builder_${this.title} scroll Previous`);
      }
    },

    /**
        * Tracks the next scrolling of a specific section's image gallery and which page
        * it was executed in Mixpanel (via Segment).
        *
        * @returns {void}
        * @private
        */

    trackUserImageScrollNext() {
      const { path } = this.$route;
      if (path === '/how-it-works/seller') {
        track(`How it works_Seller_${this.title} scroll Next`);
      } else if (path === '/how-it-works/buyer') {
        track(`How it works_Buyer_${this.title} scroll Next`);
      } else {
        track(`How it works_Builder_${this.title} scroll Next`);
      }
    },

    /**
        * Tracks the downloading of the specific document and from which page the download
        * was executed in Mixpanel (via Segment).
        *
        * @returns {void}
        * @private
        */

    trackDocumentDownload(input) {
      const { path } = this.$route;
      if (path === '/how-it-works/seller') {
        track(`How it works_Seller_${input} Download`);
      } else if (path === '/how-it-works/buyer') {
        track(`How it works_Buyer_${input} Download`);
      } else {
        track(`How it works_Builder_${input} Download`);
      }
    },
  },
};
